import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Textarea } from '@/components/ui-kit/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { useStepper } from '@/components/stepper/stepper-provider';
import { useCreateApiContext } from '../create-api-context';
import { useGetToolDetails } from '@/modules/gpt-chats/hooks/use-tools';

import { useSearchParams } from '@/lib/react-router-compat';
import { useTranslation } from 'react-i18next';

const basicInfoSchema = (t: any) =>
  z.object({
    name: z.string().trim().min(1, t('NAME_REQUIRED')),
    description: z.string().trim().min(1, t('DESCRIPTION_REQUIRED')),
    method: z.string().trim().min(1, t('METHOD_REQUIRED')),
    baseUrl: z.string().trim().url(t('INVALID_URL_FORMAT')),
    path: z
      .string()
      .trim()
      .min(1, t('PATH_REQUIRED'))
      .refine((val) => val.startsWith('/'), {
        message: t('PATH_MUST_START_WITH_SLASH'),
      }),
  });

type BasicInfoFormValues = z.infer<typeof basicInfoSchema>;

export const CreateApiBasicInfoForm = ({ toolId }: { toolId: string }) => {
  const searchParams = useSearchParams();
  const isBlocksTool = searchParams.get('isBlocksTool') === 'true';
  const { nextStep } = useStepper();
  const { t } = useTranslation();
  const { formData, updateFormData } = useCreateApiContext();
  const { data: toolDetails } = useGetToolDetails(toolId, isBlocksTool);
  const [method, setMethod] = useState(formData.method?.toUpperCase() || 'GET');
  const baseURL = toolDetails?.APIConfig?.BaseURL || '';

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<BasicInfoFormValues>({
    resolver: zodResolver(basicInfoSchema(t)),
    defaultValues: {
      name: formData.name,
      description: formData.description,
      method: formData.method || 'GET',
      baseUrl: baseURL,
      path: '',
    },
  });

  useEffect(() => {
    setValue('name', formData.name);
    setValue('description', formData.description);

    const upperMethod = formData.method?.toUpperCase() || 'GET';
    setValue('method', upperMethod);
    setMethod(upperMethod);

    if (formData.endpoint) {
      if (formData.endpoint.startsWith('http')) {
        try {
          const url = new URL(formData.endpoint);
          setValue('path', url.pathname);
          setValue('baseUrl', `${url.protocol}//${url.host}`);
        } catch (_e) {
          setValue('path', formData.endpoint);
        }
      } else {
        setValue('path', formData.endpoint);
      }
    } else {
      setValue('path', '');
    }
  }, [formData, setValue]);

  useEffect(() => {
    if (baseURL) {
      setValue('baseUrl', baseURL);
    }
  }, [baseURL, setValue]);

  const onSubmit = (data: BasicInfoFormValues) => {
    updateFormData({
      name: data.name,
      description: data.description || '',
      method: method,
      endpoint: data.path,
    });
    nextStep();
  };

  return (
    <div className="w-full max-w-3xl">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold">{t('BASIC_INFORMATION')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('BASIC_INFORMATION_DESCRIPTION')}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="name">{t('PARAM_NAME')}</Label>
          <Input
            id="name"
            placeholder={t('TYPE_HERE')}
            {...register('name')}
            className={errors.name ? 'border-error' : ''}
          />
          {errors.name && <p className="text-sm text-error">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="description">{t('DESCRIPTION')}</Label>
          </div>
          <Textarea
            id="description"
            placeholder={t('WRITE_HERE')}
            rows={4}
            {...register('description')}
            className={errors.description ? 'border-error' : ''}
          />
          {errors.description && <p className="text-sm text-error">{errors.description.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="path">{t('PATH')}</Label>
          <div className="flex gap-2">
            <Select value={method || 'GET'} onValueChange={setMethod}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="GET" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GET">GET</SelectItem>
                <SelectItem value="POST">POST</SelectItem>
                <SelectItem value="PUT">PUT</SelectItem>
                <SelectItem value="DELETE">DELETE</SelectItem>
                <SelectItem value="PATCH">PATCH</SelectItem>
              </SelectContent>
            </Select>
            <Input
              id="baseUrl"
              placeholder="https://www.example.com"
              {...register('baseUrl')}
              className={`flex-1 ${errors.baseUrl ? 'border-error' : ''}`}
              disabled={!!baseURL}
            />
            <Input
              id="path"
              placeholder={t('ENTER_PATH_PLACEHOLDER')}
              {...register('path')}
              className={`flex-1 ${errors.path ? 'border-error' : ''}`}
            />
          </div>
          {(errors.baseUrl || errors.path) && (
            <p className="text-sm text-error">{errors.baseUrl?.message || errors.path?.message}</p>
          )}
        </div>

        <div className="flex justify-start pt-4">
          <Button type="submit" className="px-8">
            {t('CONTINUE')}
          </Button>
        </div>
      </form>
    </div>
  );
};
